import { Component } from '@theme/component';

/**
 * Pagination custom element that allows navigating between pages.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} nav
 * @property {HTMLElement[]} pages
 *
 * @extends {Component<Refs>}
 */
class PaginationCustom extends Component {
  requiredRefs = ['nav', 'pages'];

  #controller = new AbortController();
  _windowSize = 0;
  _isUpdatingFromResize = false;

  constructor() {
    super();

    this._mutationObserver = new MutationObserver((muts) => this._handleMutations(muts));
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this.refs.nav);
    this._mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-page'],
    });
    this._windowSize = 0;
    this.#handleResize();

    const { signal } = this.#controller;

    window.addEventListener('locationchange', this.onLocationChange.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
    this._mutationObserver.disconnect();

    this.#controller.abort();
  }

  /**
   * @param {MutationRecord[]} _mutations
   */
  _handleMutations(_mutations) {
    if (this._isUpdatingFromResize) return; // ignore self-caused mutations

    this._windowSize = 0;
    this.#handleResize();
  }

  #handleResize = () => {
    const parentSize = Number(this.getBoundingClientRect().width);
    const windowSize = Math.max(1, Math.floor(parentSize / 50) - 5);

    if (windowSize === this._windowSize) return;

    this._windowSize = windowSize;
    this._isUpdatingFromResize = true;

    try {
      const pageNumbers = this.refs.pages.filter((el) => !el.dataset.prev && !el.dataset.next);

      if (pageNumbers.length === 0) return;

      const currentPage = this.selectedPage;
      const totalPages = this.totalPages;
      const maxPagesBeforeCurrentPage = Math.floor(windowSize / 2);
      const maxPagesAfterCurrentPage = Math.ceil(windowSize / 2) - 1;
      let startPage = Math.max(1, currentPage - maxPagesBeforeCurrentPage);
      let endPage = Math.min(totalPages, currentPage + maxPagesAfterCurrentPage);
      if (currentPage <= maxPagesBeforeCurrentPage) {
        startPage = 1;
        endPage = Math.min(totalPages, windowSize);
      } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
        startPage = Math.max(1, totalPages - windowSize + 1);
        endPage = totalPages;
      }
      let availableStartPage = -1;
      let availableEndPage = -1;
      pageNumbers.forEach((el, i) => {
        const page = Number(el.dataset.page);
        if (isNaN(page)) return;

        if (availableStartPage === -1 && i !== 0) {
          availableStartPage = page;
        }

        if (page > availableEndPage && i !== pageNumbers.length - 1) {
          availableEndPage = page;
        }

        if (i === 0 || i === pageNumbers.length - 1 || (page >= startPage && page <= endPage)) {
          if (el.parentElement) {
            el.parentElement.style.display = '';
          } else {
            el.style.display = '';
          }
        } else {
          if (el.parentElement) {
            el.parentElement.style.display = 'none';
          } else {
            el.style.display = 'none';
          }
        }
      });

      const dots = this.querySelectorAll('.pagination-dots');
      let firstDots = null;
      let lastDots = null;

      for (const el of dots) {
        const prevEl = el.previousElementSibling;
        if (!prevEl || !(prevEl instanceof HTMLElement)) continue;

        const pageBtn = prevEl.querySelector('[data-page]');
        if (!pageBtn || !(pageBtn instanceof HTMLElement)) continue;

        const prevPage = Number(pageBtn.dataset.page);

        if (!isNaN(prevPage)) {
          if (prevPage === 1) {
            firstDots = el;
          } else {
            lastDots = el;
          }
        }
      }

      const dotsTemplate = this.querySelector('#pagination-dots-template');

      if (Math.max(startPage, availableStartPage) > 2) {
        if (firstDots && firstDots instanceof HTMLElement) {
          firstDots.style.display = '';
        } else {
          if (dotsTemplate && dotsTemplate instanceof HTMLTemplateElement) {
            const firstPageEl = pageNumbers.filter((el) => !el.dataset.prev)[0];

            if (
              firstPageEl &&
              firstPageEl.parentElement &&
              firstPageEl.parentElement.parentElement
            ) {
              const clone = dotsTemplate.content.cloneNode(true);
              firstPageEl.parentElement.parentElement.insertBefore(
                clone,
                firstPageEl.parentElement.nextSibling
              );
            }
          }
        }
      } else if (firstDots && firstDots instanceof HTMLElement) {
        firstDots.style.display = 'none';
      }

      if (Math.min(availableEndPage, endPage) < totalPages - 1) {
        if (lastDots && lastDots instanceof HTMLElement) {
          lastDots.style.display = '';
        } else {
          if (dotsTemplate && dotsTemplate instanceof HTMLTemplateElement) {
            const lastPageEl = pageNumbers.filter((el) => !el.dataset.next).slice(-1)[0];

            if (lastPageEl && lastPageEl.parentElement && lastPageEl.parentElement.parentElement) {
              const clone = dotsTemplate.content.cloneNode(true);
              lastPageEl.parentElement.parentElement.insertBefore(clone, lastPageEl.parentElement);
            }
          }
        }
      } else if (lastDots && lastDots instanceof HTMLElement) {
        lastDots.style.display = 'none';
      }
    } finally {
      this._isUpdatingFromResize = false;
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   *
   * @param {number} page
   */
  _select(page, dispatchEvent = true) {
    const total = this.totalPages;
    if (page < 1 || page > total) return;

    this.refs.pages.forEach((pageEl, j) => {
      if (!(pageEl instanceof HTMLElement)) return;

      pageEl.blur();

      if (pageEl.dataset.page === String(page)) {
        pageEl.setAttribute('aria-current', 'page');
        pageEl.classList.add('is-current');
      } else {
        pageEl.removeAttribute('aria-current');
        pageEl.classList.remove('is-current');
      }

      const enableArrow = (/** @type {HTMLElement} */ el) => {
        el.removeAttribute('aria-disabled');
        el.removeAttribute('tabindex');
        el.classList.remove('is-disabled', 'button-secondary');
        el.classList.add('button-primary');
      };

      const disableArrow = (/** @type {HTMLElement} */ el) => {
        el.setAttribute('aria-disabled', 'true');
        el.setAttribute('tabindex', '-1');
        el.classList.add('is-disabled', 'button-secondary');
        el.classList.remove('button-primary');
      };

      if (pageEl.dataset.prev) {
        if (page > 1) {
          pageEl.dataset.page = String(page - 1);
          enableArrow(pageEl);
        } else {
          disableArrow(pageEl);
        }
      }
      if (pageEl.dataset.next) {
        if (page < total) {
          pageEl.dataset.page = String(page + 1);
          enableArrow(pageEl);
        } else {
          disableArrow(pageEl);
        }
      }
    });

    if (dispatchEvent) {
      this.dispatchEvent(new CustomEvent('change', { detail: { page } }));
    }
  }

  onLocationChange() {
    const searchParams = new URLSearchParams(window.location.search);
    const page = searchParams.get('page');

    if (!page) return;

    const currentPage = this.selectedPage;
    const totalPages = this.totalPages;

    const pageNumber = Number(page);
    if (
      isNaN(pageNumber) ||
      pageNumber < 1 ||
      pageNumber > totalPages ||
      pageNumber === currentPage
    )
      return;

    this._select(pageNumber, false);
  }

  /**
   * Handle page click events.
   * @param {MouseEvent} e - The click event.
   */
  onPageClick(e) {
    if (!(e.target instanceof HTMLAnchorElement)) return;

    const page = e.target.dataset.page;
    if (page) {
      e.preventDefault();
      this._select(Number(page));
    }
  }

  /**
   * Handle prev page click events.
   * @param {MouseEvent} e - The click event.
   */
  onPrevPageClick(e) {
    if (
      !(e.target instanceof HTMLAnchorElement) ||
      e.target.getAttribute('aria-disabled') === 'true'
    )
      return;
    this.onPageClick(e);
  }

  /**
   * Handle next page click events.
   * @param {MouseEvent} e - The click event.
   */
  onNextPageClick(e) {
    if (
      !(e.target instanceof HTMLAnchorElement) ||
      e.target.getAttribute('aria-disabled') === 'true'
    )
      return;
    this.onPageClick(e);
  }

  /**
   * Handle keydown events on the tab elements.
   * @param {KeyboardEvent} e - The keydown event.
   */
  onPageKeydown(e) {
    if (!(e.target instanceof HTMLElement)) return;

    const current = this.refs.pages.indexOf(e.target);
    if (current === -1) return;

    let next = null;
    switch (e.key) {
      case 'ArrowRight':
        next = Math.max(0, Math.min(current + 1, this.refs.pages.length - 1));
        break;
      case 'ArrowLeft':
        next = Math.max(0, Math.min(current - 1, this.refs.pages.length - 1));
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.refs.pages.length - 1;
        break;
      case 'Enter':
      case ' ':
        if (e.target.dataset.page) {
          this._select(Number(e.target.dataset.page));
          e.preventDefault();
        }
        return;
      default:
        return;
    }
    if (next !== null && this.refs.pages[next]) {
      this.refs.pages[next]?.focus();
      e.preventDefault();
    }
  }

  get selectedPage() {
    const pageEl = this.refs.pages?.find((t) => t.getAttribute('aria-current') === 'page');
    return pageEl ? Number(pageEl.dataset.page) : -1;
  }
  set selectedPage(page) {
    if (this.refs.pages && page >= 0 && page <= this.totalPages) this._select(page);
  }
  get totalPages() {
    return this.refs.pages.reduce((acc, page) => Math.max(acc, Number(page.dataset.page)), 0);
  }
}

customElements.define('pagination-custom', PaginationCustom);
